import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useApi } from "@/core/api";
import type {
  IPushStAdjustmentsResponse,
  IStAdjustmentPushProgress,
  IStAdjustmentsPage,
  IStAdjustmentsSummaryResponse,
} from "./types";
import { shouldPollPushStatus } from "./utils";

// Cadence of the push status poll — one small request, so it can be brisk.
export const PUSH_POLL_INTERVAL = 1500;

// How long to keep waiting for an enqueued push to report itself before telling
// the user we've lost track of it. Generous, because the worker has to build the
// proposal list (reading the workbook) before it records anything.
export const PUSH_START_GRACE_MS = 90_000;

// Lines per page in the drill-down table.
export const LINES_PAGE_SIZE = 50;

const runBasePath = (businessId: string, runId: string) =>
  `/businesses/${businessId}/ai_script_runs/${runId}`;

// RESTful sub-resources of a run: the per-line proposals (index), their rollup
// summary (a singular resource), and a push (a resource whose create starts one
// and show reports progress).
export const stAdjustmentsPath = (businessId: string, runId: string) =>
  `${runBasePath(businessId, runId)}/st_adjustments`;
export const stAdjustmentsSummaryPath = (businessId: string, runId: string) =>
  `${runBasePath(businessId, runId)}/st_adjustments_summary`;
export const stAdjustmentPushPath = (businessId: string, runId: string) =>
  `${runBasePath(businessId, runId)}/st_adjustment_push`;

// Prefix covering every lines query of a run: the unfiltered list plus one per
// drilled-down technician. Invalidate on this — the exact key below ends in the
// employee id, so invalidating it would leave every open drill-down stale.
export const stAdjustmentLinesRunKey = (
  businessId: string | null,
  runId: string | null,
) => ["st-adjustment-lines", businessId, runId] as const;

// `employeeId` is part of the key, not just the request: a technician's filtered
// pages and the run's unfiltered pages are different lists, and sharing one cache
// entry would splice them together as the drill-down opens and closes.
export const stAdjustmentLinesKey = (
  businessId: string | null,
  runId: string | null,
  employeeId: number | null = null,
) => [...stAdjustmentLinesRunKey(businessId, runId), employeeId] as const;

export const stAdjustmentsSummaryKey = (
  businessId: string | null,
  runId: string | null,
) => ["st-adjustments-summary", businessId, runId] as const;

export const stAdjustmentPushKey = (
  businessId: string | null,
  runId: string | null,
) => ["st-adjustment-push", businessId, runId] as const;

// The individual commissions/spiffs this run would push, with live amounts —
// offset-paginated, because a period can carry thousands of lines. Only fetched
// when `enabled` (e.g. the technician sidebar is open).
//
// Deliberately not polled while a push runs. An infinite query refetches EVERY
// loaded page on each tick, and the server rebuilds the whole proposal list (a
// workbook read) per page request — a dozen loaded pages would mean a dozen
// rebuilds every few seconds, competing with the worker doing the actual push.
// The header action and toast messages carry live status instead, and the rows
// refresh once when the push completes (see `useStAdjustmentPush`).
// `employeeId` narrows to one technician's lines — the drill-down behind a rollup
// row. The server filters and counts against that technician, so `total_count`
// and `has_more` describe their lines rather than the whole run's.
export const useStAdjustmentLines = (
  businessId: string | null,
  runId: string | null,
  enabled = true,
  employeeId: number | null = null,
) => {
  const api = useApi();

  return useInfiniteQuery({
    queryKey: stAdjustmentLinesKey(businessId, runId, employeeId),
    queryFn: ({ pageParam }) => {
      if (!businessId || !runId) {
        throw new Error("Payroll run is required");
      }

      return api
        .get<IStAdjustmentsPage>(stAdjustmentsPath(businessId, runId), {
          params: {
            page: pageParam,
            page_size: LINES_PAGE_SIZE,
            ...(employeeId !== null && { employee_id: employeeId }),
          },
        })
        .then((res) => res.data);
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.has_more ? lastPage.next_page : undefined,
    enabled: enabled && !!businessId && !!runId,
    retry: false,
  });
};

// Aggregate rollups (totals + by technician) for a run's pushable
// adjustments. Bounded by the number of technicians and categories rather than
// the number of lines, so this stays the scale-safe overview.
export const useStAdjustmentsSummary = (
  businessId: string | null,
  runId: string | null,
  enabled = true,
) => {
  const api = useApi();

  return useQuery({
    queryKey: stAdjustmentsSummaryKey(businessId, runId),
    queryFn: () => {
      if (!businessId || !runId) {
        throw new Error("Payroll run is required");
      }

      return api
        .get<IStAdjustmentsSummaryResponse>(
          stAdjustmentsSummaryPath(businessId, runId),
        )
        .then((res) => res.data);
    },
    enabled: enabled && !!businessId && !!runId,
    retry: false,
    // This rollup is a live projection of workbook cells, not a document of its
    // own — every amount is read from the Grid at request time. A Grid edit
    // changes it with no event reaching this query, so under the app-wide
    // 5-minute staleTime the tab served figures from before the edit.
    //
    // That is how a released PAY gate kept reporting "amount is zero" here while
    // the Grid showed the very same cells released: the numbers were not wrong,
    // they were from five minutes ago. On a payroll surface a stale figure is
    // worse than a slow one, so this always refetches.
    //
    // The per-line query below deliberately keeps the default: it is an infinite
    // query, so refetching rebuilds the proposal list once per loaded page, and
    // it is invalidated explicitly after a push.
    staleTime: 0,
  });
};

// Live progress of the run's most recent push. Polls while a batch is queued or
// running — and while `awaitingStart` covers the enqueue-to-worker-start gap,
// where the server still reports the previous state (see `shouldPollPushStatus`).
export const useStAdjustmentPushStatus = (
  businessId: string | null,
  runId: string | null,
  awaitingStart = false,
) => {
  const api = useApi();

  return useQuery({
    queryKey: stAdjustmentPushKey(businessId, runId),
    queryFn: () => {
      if (!businessId || !runId) {
        throw new Error("Payroll run is required");
      }

      return api
        .get<IStAdjustmentPushProgress>(stAdjustmentPushPath(businessId, runId))
        .then((res) => res.data);
    },
    enabled: !!businessId && !!runId,
    retry: false,
    refetchInterval: (query) =>
      shouldPollPushStatus(query.state.data?.status, awaitingStart)
        ? PUSH_POLL_INTERVAL
        : false,
  });
};

// Starts a background push. `technicianIds` pushes those technicians' pending
// lines; omitting it pushes every not-yet-pushed line in the run. Both are
// resolved server-side, so neither enumerates thousands of line keys.
//
// The endpoint also accepts an explicit `selected` list of line keys, which no
// client path uses since the per-line push dialog gave way to the by-technician
// tab. Re-add it here if spot-pushing individual lines comes back.
//
// Fetches the status once on success so a worker that starts quickly shows up
// right away. It deliberately does not seed an optimistic "queued" record: the
// worker hasn't counted the lines yet, so a fabricated status renders "0 of 0"
// and is immediately overwritten by the server. Callers track the in-between
// state themselves — see `useStAdjustmentPush`.
export const useStartStAdjustmentPush = (
  businessId: string | null,
  runId: string | null,
) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ technicianIds }: { technicianIds?: number[] }) => {
      if (!businessId || !runId) {
        throw new Error("Payroll run is required");
      }

      const body: Record<string, unknown> = {};
      if (technicianIds?.length) body.technician_ids = technicianIds;

      return api
        .post<IPushStAdjustmentsResponse>(
          stAdjustmentPushPath(businessId, runId),
          body,
        )
        .then((res) => res.data);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: stAdjustmentPushKey(businessId, runId),
      });
    },
  });
};
